import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  ProgressBar,
  Spinner,
  Text,
} from "@fluentui/react-components";
import {
  CheckmarkCircle20Filled,
  DismissCircle20Filled,
} from "@fluentui/react-icons";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { getProjectStatus, scoreProject } from "../../decoodaapi";
import { IProject } from "./projectCard.types";
import { ProjectStatus } from "./projectConstants";
import { useScoreProjectDialogStyles } from "./scoreProjectDialog.styles";

interface IScoreProjectDialogProps {
  isOpen: boolean;
  project: IProject;
  onDismiss: () => void;
  onScoringStarted: () => void;
  onScoringComplete: () => void;
}

type ScoringState =
  | "idle"
  | "starting"
  | "in_progress"
  | "completed"
  | "error"
  | "rescore_confirm";

interface StatusResponse {
  status: ProjectStatus;
  progress?: number;
  message?: string;
}

export const ScoreProjectDialog = ({
  isOpen,
  project,
  onDismiss,
  onScoringStarted,
  onScoringComplete,
}: IScoreProjectDialogProps): JSX.Element => {
  const styles = useScoreProjectDialogStyles();
  const [scoringState, setScoringState] = useState<ScoringState>("idle");
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // Use refs to avoid stale closure issues with setInterval
  // useLayoutEffect ensures synchronous update before any effects run
  const onScoringCompleteRef = useRef(onScoringComplete);
  useLayoutEffect(() => {
    onScoringCompleteRef.current = onScoringComplete;
  }, [onScoringComplete]);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const pollStatus = useCallback(async () => {
    try {
      const response: StatusResponse = await getProjectStatus({
        projectId: project.id,
      });

      if (response.status === ProjectStatus.Scored) {
        setScoringState("completed");
        setProgress(100);
        setStatusMessage("Scoring completed successfully!");
        setErrorMessage(null);
        stopPolling();
        try {
          onScoringCompleteRef.current();
        } catch (callbackError) {
          console.error("Error in onScoringComplete callback:", callbackError);
        }
      } else if (response.status === ProjectStatus.InProgress) {
        setScoringState("in_progress");
        if (response.progress !== undefined) {
          setProgress(response.progress);
        }
        setStatusMessage(response.message || "In Progress...");
        setErrorMessage(null);
      } else if (response.status === ProjectStatus.Error) {
        setScoringState("error");
        setErrorMessage(
          response.message ||
            "An error occurred during scoring. Please try again.",
        );
        setProgress(0);
        stopPolling();
      } else if (response.status === ProjectStatus.NotScored) {
        setStatusMessage("Waiting for scoring to start...");
      }
    } catch (error) {
      console.error("Error polling project status:", error);
      setScoringState("error");
      setErrorMessage(
        "Failed to retrieve scoring status. Please try again later.",
      );
      stopPolling();
    }
  }, [project.id, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    pollStatus();
    pollingRef.current = setInterval(pollStatus, 3000);
  }, [pollStatus, stopPolling]);

  const handleStartScoring = () => {
    onScoringStarted();
    onDismiss();

    scoreProject({ projectId: project.id }).catch((error) => {
      console.error("Error starting scoring:", error);
    });
  };

  const handleClose = () => {
    stopPolling();
    onDismiss();
  };

  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  useEffect(() => {
    if (isOpen) {
      // Reset to appropriate state based on project status
      if (project.status === ProjectStatus.InProgress) {
        setScoringState("in_progress");
        setStatusMessage("In Progress...");
        setProgress(0);
        setErrorMessage(null);
        startPolling();
      } else if (
        project.status === ProjectStatus.Scored ||
        project.status === ProjectStatus.Error
      ) {
        // Show re-score confirmation for already scored projects or projects with errors
        setScoringState("rescore_confirm");
        setStatusMessage("");
        setProgress(0);
        setErrorMessage(null);
      } else {
        // Not Scored - reset to idle
        setScoringState("idle");
        setStatusMessage("");
        setProgress(0);
        setErrorMessage(null);
      }
    } else {
      // Dialog closed - stop polling
      stopPolling();
    }
  }, [isOpen, project.status, project.id, startPolling, stopPolling]);

  const renderIdleState = () => (
    <>
      <Text size={300} weight="semibold">
        You are about to score project '{project.name}'
      </Text>
      <div className={styles.warningBox}>
        <Text size={200}>
          This step will analyze all data in your project using AI to
          automatically discover topics, sentiment, and emotions. The process
          runs in the background and may take several minutes depending on your
          data volume.
        </Text>
      </div>
    </>
  );

  const renderRescoreConfirmState = () => (
    <>
      <Text size={300} weight="semibold">
        You are about to re-score project '{project.name}'
      </Text>
      <div className={styles.infoSection}>
        <Text weight="semibold">Rescoring will:</Text>
        <ul className={styles.warningList}>
          <li>
            <Text size={200}>Delete your current topic structure</Text>
          </li>
          <li>
            <Text size={200}>Re-analyze all data from the beginning</Text>
          </li>
          <li>
            <Text size={200}>Generate a completely new set of topics</Text>
          </li>
        </ul>
      </div>
      <div className={styles.warningBox}>
        <Text size={200}>
          Any manual topic edits, merges, or refinements will not be preserved.
          Are you sure you want to continue?
        </Text>
      </div>
    </>
  );

  const renderProgressState = () => (
    <div className={styles.progressSection}>
      {scoringState === "starting" ? (
        <Spinner size="medium" label="Starting scoring process..." />
      ) : (
        <>
          <div className={styles.statusHeader}>
            <Spinner size="small" />
            <Text weight="semibold">{statusMessage}</Text>
          </div>
          <ProgressBar value={progress / 100} className={styles.progressBar} />
          <Text size={200} className={styles.progressText}>
            {progress > 0 ? `${progress}% complete` : "Processing..."}
          </Text>
        </>
      )}
    </div>
  );

  const renderCompletedState = () => (
    <div className={styles.completedSection}>
      <CheckmarkCircle20Filled className={styles.successColor} />
      <Text weight="semibold" className={styles.successColor}>
        Scoring completed successfully!
      </Text>
      <Text size={200}>
        The project has been scored. You can now view the results or export the
        data.
      </Text>
    </div>
  );

  const renderErrorState = () => (
    <div className={styles.errorSection}>
      <DismissCircle20Filled className={styles.errorColor} />
      <Text weight="semibold" className={styles.errorColor}>
        Scoring failed
      </Text>
      <Text size={200}>{errorMessage}</Text>
    </div>
  );

  const renderContent = () => {
    switch (scoringState) {
      case "idle":
        return renderIdleState();
      case "rescore_confirm":
        return renderRescoreConfirmState();
      case "starting":
      case "in_progress":
        return renderProgressState();
      case "completed":
        return renderCompletedState();
      case "error":
        return renderErrorState();
      default:
        return null;
    }
  };

  const renderActions = () => {
    switch (scoringState) {
      case "idle":
      case "rescore_confirm":
        return (
          <>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={handleClose}>
                Cancel
              </Button>
            </DialogTrigger>
            <Button appearance="primary" onClick={handleStartScoring}>
              Score
            </Button>
          </>
        );
      case "starting":
      case "in_progress":
        return (
          <Button appearance="secondary" onClick={handleClose}>
            Close
          </Button>
        );
      case "completed":
        return (
          <Button appearance="primary" onClick={handleClose}>
            Close
          </Button>
        );
      case "error":
        return (
          <>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" onClick={handleClose}>
                Cancel
              </Button>
            </DialogTrigger>
            <Button appearance="primary" onClick={handleStartScoring}>
              Retry
            </Button>
          </>
        );
      default:
        return null;
    }
  };

  const getDialogTitle = () => {
    if (scoringState === "rescore_confirm") {
      return "Re-score Project";
    }
    return "Score Project";
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) onDismiss();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{getDialogTitle()}</DialogTitle>
          <DialogContent className={styles.content}>
            {renderContent()}
          </DialogContent>
          <DialogActions>{renderActions()}</DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
